import { useEffect, useState } from "react";
import { apiFetchPublic } from "../lib/api";
import { FEATURED_VIDEOS } from "../data/homePage";
import { videoToFilm } from "./useVideos";

/** @typedef {{
 *   slot: number,
 *   youtubeVideoId?: string,
 *   title?: string,
 *   thumbnailUrl?: string | null,
 *   caption: string,
 *   background?: string,
 *   href?: string,
 * }} FeaturedVideoDisplay */

/**
 * @param {import("./useVideos").VideoRecord | undefined} video
 * @param {typeof FEATURED_VIDEOS[number]} fallback
 * @param {number} slot
 * @returns {FeaturedVideoDisplay}
 */
function videoToDisplay(video, fallback, slot) {
  if (video?.youtubeVideoId) {
    const film = videoToFilm(video);
    const caption = film.title ? `[ VIDEO · ${film.title} ]` : fallback.caption;
    return {
      slot,
      youtubeVideoId: video.youtubeVideoId,
      title: film.title,
      thumbnailUrl: film.thumbnailUrl,
      caption,
      href: `/videos?v=${encodeURIComponent(video.youtubeVideoId)}`,
    };
  }

  return {
    slot,
    caption: fallback.caption,
    background: fallback.background,
  };
}

/** @returns {{ videos: FeaturedVideoDisplay[], loading: boolean }} */
export function useHomeFeaturedVideos() {
  const [videos, setVideos] = useState(/** @type {FeaturedVideoDisplay[] | null} */ (null));

  useEffect(() => {
    let cancelled = false;

    async function loadVideos() {
      try {
        const body = await apiFetchPublic("/home-featured-videos");
        const apiVideos = body.data?.videos || [];

        if (!cancelled) {
          const display = Array.from({ length: 2 }, (_, index) => {
            const slot = index + 1;
            const video = apiVideos.find((item) => Number(item.featuredSlot) === slot);
            const fallback = FEATURED_VIDEOS[index];
            return videoToDisplay(video, fallback, slot);
          });
          setVideos(display);
        }
      } catch {
        if (!cancelled) {
          setVideos(
            FEATURED_VIDEOS.map((fallback, index) => ({
              slot: index + 1,
              caption: fallback.caption,
              background: fallback.background,
            }))
          );
        }
      }
    }

    void loadVideos();

    return () => {
      cancelled = true;
    };
  }, []);

  return {
    videos: videos ?? [],
    loading: videos === null,
  };
}
